import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

interface Customer {
  id: string;
  name: string;
  contact: string;
  shootingDate: string;
  shootingType: string;
  photographer: string;
  concept: string;
  status: string;
  orderSheetComplete: boolean;
  checklistComplete: boolean;
  deliveryStatus: string;
}

export default function StudioCustomersPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('전체');

  const customers: Customer[] = [
    {
      id: '1',
      name: '김지은',
      contact: '010-1234-5678',
      shootingDate: '2024-01-25',
      shootingType: '리허설',
      photographer: '박작가',
      concept: '내추럴',
      status: '촬영완료',
      orderSheetComplete: true,
      checklistComplete: true,
      deliveryStatus: '전달완료'
    },
    {
      id: '2',
      name: '이민수',
      contact: '010-2345-6789',
      shootingDate: '2024-01-28',
      shootingType: '본식스냅',
      photographer: '최작가',
      concept: '클래식',
      status: '촬영예정',
      orderSheetComplete: true,
      checklistComplete: false,
      deliveryStatus: '대기중'
    },
    {
      id: '3',
      name: '박영희',
      contact: '010-3456-7890',
      shootingDate: '2024-02-01',
      shootingType: '본식영상',
      photographer: '정작가',
      concept: '모던',
      status: '촬영예정',
      orderSheetComplete: false,
      checklistComplete: false,
      deliveryStatus: '대기중'
    },
    {
      id: '4',
      name: '최수진',
      contact: '010-4567-8901',
      shootingDate: '2024-01-20',
      shootingType: '스냅+영상',
      photographer: '박작가',
      concept: '로맨틱',
      status: '촬영완료',
      orderSheetComplete: true,
      checklistComplete: true,
      deliveryStatus: '선택대기'
    }
  ];

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.contact.includes(searchTerm);
    const matchesStatus = statusFilter === '전체' || customer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusOptions = ['전체', '촬영예정', '촬영완료', '편집중', '전달완료'];

  const handleCustomerClick = (customerId: string) => {
    navigate(`/dashboard/studio-customer-detail?id=${customerId}`);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">고객별 촬영 정보 관리</h1>
            <p className="text-sm text-gray-600 mt-1">촬영 오더시트 및 고객 정보를 관리하세요</p>
          </div>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">총 고객수</p>
                <p className="text-2xl font-bold text-gray-800">{customers.length}명</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-line text-xl text-blue-600"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">촬영 예정</p>
                <p className="text-2xl font-bold text-orange-600">
                  {customers.filter(c => c.status === '촬영예정').length}건
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-camera-line text-xl text-orange-600"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">촬영 완료</p>
                <p className="text-2xl font-bold text-green-600">
                  {customers.filter(c => c.status === '촬영완료').length}건
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-check-line text-xl text-green-600"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">전달 완료</p>
                <p className="text-2xl font-bold text-purple-600">
                  {customers.filter(c => c.deliveryStatus === '전달완료').length}건
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-download-cloud-line text-xl text-purple-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* 검색 및 필터 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                <input
                  type="text"
                  placeholder="고객명, 연락처로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              {statusOptions.map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 고객 목록 */}
        <div className="space-y-4">
          {filteredCustomers.map((customer) => (
            <div 
              key={customer.id} 
              className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleCustomerClick(customer.id)}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-camera-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-bold text-gray-800">{customer.name}</h3>
                      <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                        customer.status === '촬영완료' ? 'bg-green-100 text-green-700' :
                        customer.status === '촬영예정' ? 'bg-orange-100 text-orange-700' :
                        customer.status === '편집중' ? 'bg-blue-100 text-blue-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {customer.status}
                      </span>
                      <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                        customer.shootingType === '리허설' ? 'bg-purple-100 text-purple-700' :
                        customer.shootingType === '본식스냅' ? 'bg-blue-100 text-blue-700' :
                        customer.shootingType === '본식영상' ? 'bg-green-100 text-green-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {customer.shootingType}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                        <span>{customer.contact}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                        <span>촬영일: {customer.shootingDate}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                        <span>작가: {customer.photographer}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">컨셉</div>
                  <div className="text-sm font-bold text-gray-800">{customer.concept}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">오더시트</div>
                  <div className={`text-sm font-bold ${customer.orderSheetComplete ? 'text-green-600' : 'text-orange-600'}`}>
                    {customer.orderSheetComplete ? '작성완료' : '미작성'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">체크리스트</div>
                  <div className={`text-sm font-bold ${customer.checklistComplete ? 'text-green-600' : 'text-orange-600'}`}>
                    {customer.checklistComplete ? '완료' : '진행중'}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">전달상태</div>
                  <div className={`text-sm font-bold ${
                    customer.deliveryStatus === '전달완료' ? 'text-green-600' :
                    customer.deliveryStatus === '선택대기' ? 'text-blue-600' :
                    'text-gray-600'
                  }`}>
                    {customer.deliveryStatus}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">작가</div>
                  <div className="text-sm font-bold text-gray-800">{customer.photographer}</div>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <div className="text-blue-600 font-medium cursor-pointer">상세보기 →</div>
              </div>
            </div>
          ))}
        </div>

        {filteredCustomers.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <i className="ri-camera-line text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">검색 조건에 맞는 고객이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
}